import React from 'react';
import { CardButton, CardColor, Modal, ModalContentHeader } from '@signaturit/react-components-library';
import { useTranslation } from 'react-i18next';
import * as singleSpa from 'single-spa';
import SmsIcon from '@material-ui/icons/SmsOutlined';
import EmailOutlinedIcon from '@material-ui/icons/EmailOutlined';
import CheckCircleOutlineOutlinedIcon from '@material-ui/icons/CheckCircleOutlineOutlined';
import { useNewRequestModalStyles } from './NewRequestModal.styles';
import { SENDBOARD_UPLOAD } from '../../constants/productsUrlHashes';
import { DataTrackerEvents } from '../../dataTracker/DataTrackerEvents';
import { sendDataTrackerEventHandler } from '../../dataTracker/DataTrackerHelper';
import { ActiveProduct } from '../../../models/ActiveProduct';
import { MenuProductTypes, ProductTypes } from '../../constants/ProductTypes';
import { hasCreditsAvailable, isProductAvailable } from '../Gauges/gaugesHelper';
import { ModalCardButton, ModalCardButtonActions } from '../../constants/ModalCardButtonType';
import { AdvancedSignatureIcon } from '../AdvancedSignatureIcon/AdvancedSignatureIcon';
import { SETTINGS_UPGRADE_URL } from '../../../config/routes';
import { openInNewTab, openInSameTab, getUrlByLanguage } from './NewRequestModalHelper';
import { canShowOfflinePaymentButton } from '../ManageCredits/ManageCreditsHelper';
import { useSelector } from 'react-redux';
import { selectUserInfo } from '../../store/User/reducer';
import { LicenseCodename } from '../../constants/LicenseCodename';

export type NewRequestModalProps = {
    open: boolean;
    onClose: (isClosed: boolean) => void;
    onClickCertifiedEmail: () => void;
    onClickCertifiedSms: () => void;
    onTemplateCertified: () => void;
    activeProducts: ActiveProduct[];
    requestOrigin: keyof ModalCardButtonActions;
    isTrialOver: boolean;
    qualifiedSignatures: boolean;
};

export const NewRequestModal = ({
    open,
    onClose,
    onClickCertifiedEmail,
    onClickCertifiedSms,
    onTemplateCertified,
    activeProducts,
    requestOrigin,
    isTrialOver,
    qualifiedSignatures,
}: NewRequestModalProps) => {
    const { t } = useTranslation();
    const classes = useNewRequestModalStyles();

    const FROM_TEMPLATES = 'fromTemplates';
    const MODAL_TITLE =
        requestOrigin === FROM_TEMPLATES
            ? 'default.new_request.templates_modal_title'
            : 'default.new_request.modal_title';
    const DATA_SIGNA_MODAL = 'new-request-modal';
    const NOT_TRIED_FEATURE_TITLE = 'default.new_request.unavailable_title';
    const HAS_SPENT_ALL_CREDITS_TITLE = 'default.new_request.spent_all_credits';
    const EMAIL_LINK = 'mailto:petitions@signaturit.com';

    const userInfo = useSelector(selectUserInfo);
    const isOfflinePayment = canShowOfflinePaymentButton(userInfo);

    const validQualifiedLicenses = [
        LicenseCodename.businessPlus,
        LicenseCodename.businessPlus2020,
        LicenseCodename.enterprise,
    ];
    const isValidQualifiedLicense = userInfo && validQualifiedLicenses.includes(userInfo.codename);

    const TRIAL_OVER_TITLE = 'default.new_request.expired_trial';
    const ADVANCED_BUTTON_TITLE =
        qualifiedSignatures && isValidQualifiedLicense
            ? 'default.new_request.qualified_advanced_signature'
            : 'default.new_request.advanced_signature';

    const shouldSendQualifiedDataTrackerEvent = qualifiedSignatures && isValidQualifiedLicense;
    const dataLicense = userInfo?.codename;
    const dataCompany = userInfo?.companyName;
    const dataEmail = userInfo?.email;
    const dataAccount = userInfo?.accountId;

    const cardButtons: ModalCardButton[] = [
        {
            title: ADVANCED_BUTTON_TITLE,
            description: 'default.new_request.biometric_signature',
            unavailableDescription: 'default.new_request.advanced_signature_unavailable',
            icon: <AdvancedSignatureIcon />,
            arrowIcon: true,
            name: `${DATA_SIGNA_MODAL}-button-advanced`,
            actions: {
                fromRequest: (type: MenuProductTypes) => {
                    closeModal();
                    singleSpa.navigateToUrl(`${SENDBOARD_UPLOAD}${type}`);
                    if (shouldSendQualifiedDataTrackerEvent) {
                        sendDataTrackerEventHandler(DataTrackerEvents.newQualifiedRequestButtonClicked, {
                            dataLicense,
                            dataCompany,
                            dataEmail,
                            dataAccount,
                        });
                    }
                },
                fromTemplates: (type: MenuProductTypes) => {
                    window.localStorage.setItem('isTemplatesManagement', 'true');
                    window.localStorage.setItem('isCreatingATemplate', 'true');
                    closeModal();

                    singleSpa.navigateToUrl(`${SENDBOARD_UPLOAD}${type}`);
                    if (shouldSendQualifiedDataTrackerEvent) {
                        sendDataTrackerEventHandler(DataTrackerEvents.newQualifiedRequestButtonClicked, {
                            dataLicense,
                            dataCompany,
                            dataEmail,
                            dataAccount,
                        });
                    }
                },
                unAvailable: (type: MenuProductTypes) => {
                    openInNewTab(getUrlByLanguage(type));
                },
                purchaseCredits: () => {
                    isOfflinePayment ? openInSameTab(EMAIL_LINK) : openInSameTab(SETTINGS_UPGRADE_URL);
                },
            },
        },
        {
            title: 'default.new_request.simple_signature',
            description: 'default.new_request.sign_with_click',
            unavailableDescription: 'default.new_request.sign_with_click_unavailable',
            icon: <CheckCircleOutlineOutlinedIcon />,
            arrowIcon: true,
            name: `${DATA_SIGNA_MODAL}-button-simple`,
            actions: {
                fromRequest: (type: MenuProductTypes) => {
                    window.localStorage.setItem('isSimpleSignature', 'true');
                    closeModal();
                    singleSpa.navigateToUrl(`${SENDBOARD_UPLOAD}${type}`);
                },
                fromTemplates: (type: MenuProductTypes) => {
                    window.localStorage.setItem('isSimpleSignature', 'true');
                    window.localStorage.setItem('isTemplatesManagement', 'true');
                    window.localStorage.setItem('isCreatingATemplate', 'true');
                    closeModal();
                    singleSpa.navigateToUrl(`${SENDBOARD_UPLOAD}${type}`);
                },
                unAvailable: (type: MenuProductTypes) => {
                    openInNewTab(getUrlByLanguage(type));
                },
                purchaseCredits: () => {
                    isOfflinePayment ? openInSameTab(EMAIL_LINK) : openInSameTab(SETTINGS_UPGRADE_URL);
                },
            },
        },
        {
            title: 'default.new_request.certified_email',
            description: 'default.new_request.certify_communications',
            unavailableDescription: 'default.new_request.certify_communications_unavailable',
            icon: <EmailOutlinedIcon />,
            arrowIcon: true,
            name: `${DATA_SIGNA_MODAL}-button-certified`,
            actions: {
                fromRequest: () => {
                    onClickCertifiedEmail();
                },
                fromTemplates: () => {
                    window.localStorage.setItem('isTemplatesManagement', 'true');
                    window.localStorage.setItem('isCreatingATemplate', 'true');
                    onTemplateCertified();
                    closeModal();
                },
                unAvailable: (type: MenuProductTypes) => {
                    openInNewTab(getUrlByLanguage(type));
                },
                purchaseCredits: () => {
                    isOfflinePayment ? openInSameTab(EMAIL_LINK) : openInSameTab(SETTINGS_UPGRADE_URL);
                },
            },
        },
        {
            title: 'default.new_request.certified_sms',
            description: 'default.new_request.certify_sms_messages',
            unavailableDescription: 'default.new_request.certify_sms_unavailable',
            icon: <SmsIcon />,
            arrowIcon: true,
            name: `${DATA_SIGNA_MODAL}-button-sms`,
            actions: {
                fromRequest: () => {
                    onClickCertifiedSms();
                },
                fromTemplates: () => {},
                unAvailable: (type: MenuProductTypes) => {
                    openInNewTab(getUrlByLanguage(type));
                },
                purchaseCredits: () => {
                    isOfflinePayment ? openInSameTab(EMAIL_LINK) : openInSameTab(SETTINGS_UPGRADE_URL);
                },
            },
        },
    ]
        .filter(
            ({ title }) => !(requestOrigin === FROM_TEMPLATES && title.split('.').pop() === ProductTypes.CertifiedSms)
        )
        .map((cardButton, order) => {
            const productType = cardButton.title.includes('advanced_signature')
                ? ('advanced_signature' as MenuProductTypes)
                : (cardButton.title.split('.').pop() as MenuProductTypes);
            const hasPurchasedCredits = activeProducts ? isProductAvailable(productType, activeProducts) : false;
            const hasCreditsLeft = activeProducts ? hasCreditsAvailable(productType, activeProducts) : false;
            return {
                ...cardButton,
                hasPurchasedCredits,
                hasCreditsLeft,
                order,
            };
        })
        .sort((a, b) => {
            if (a.hasPurchasedCredits !== b.hasPurchasedCredits) {
                return a.hasPurchasedCredits ? -1 : 1;
            } else if (a.hasCreditsLeft !== b.hasCreditsLeft) {
                return a.hasCreditsLeft ? -1 : 1;
            }
            return a.order - b.order;
        });

    const closeModal = (): void => {
        onClose(true);
    };

    const handleCardButtonClick = (cardButton: ModalCardButton): void => {
        const { title } = cardButton;
        const type = title.includes('advanced_signature')
            ? ('advanced_signature' as MenuProductTypes)
            : (title.split('.').pop() as MenuProductTypes);

        sendDataTrackerEventHandler(DataTrackerEvents.typeOfRequestHasBeenSelected, type);

        if (isTrialOver) {
            openInSameTab(SETTINGS_UPGRADE_URL);
        } else if (!cardButton.hasPurchasedCredits) {
            cardButton.actions.unAvailable(type);
        } else if (!cardButton.hasCreditsLeft) {
            cardButton.actions.purchaseCredits();
        } else {
            cardButton.actions[requestOrigin](type);
        }
    };

    const getCardTitle = (cardButton: ModalCardButton): string => {
        if (isTrialOver) {
            return TRIAL_OVER_TITLE;
        } else if (!cardButton.hasPurchasedCredits) {
            return NOT_TRIED_FEATURE_TITLE;
        } else if (!cardButton.hasCreditsLeft) {
            return HAS_SPENT_ALL_CREDITS_TITLE;
        } else {
            return cardButton.title;
        }
    };

    return (
        <Modal opened={open} onClickClose={closeModal} dataName={DATA_SIGNA_MODAL}>
            <ModalContentHeader>{t(MODAL_TITLE)}</ModalContentHeader>
            <div>
                {cardButtons.map((cardButton: ModalCardButton) => {
                    const { title, icon, arrowIcon, name, hasPurchasedCredits, hasCreditsLeft } = cardButton;

                    const unavailableProduct = !hasPurchasedCredits || isTrialOver || !hasCreditsLeft;

                    return (
                        <div className={classes.cardButton} key={title}>
                            <CardButton
                                title={t(getCardTitle(cardButton))}
                                description={t(
                                    unavailableProduct ? cardButton.unavailableDescription : cardButton.description
                                )}
                                icon={icon}
                                arrowIcon={arrowIcon}
                                name={name}
                                color={CardColor.tertiary}
                                onClick={() => handleCardButtonClick(cardButton)}
                            />
                        </div>
                    );
                })}
            </div>
        </Modal>
    );
};
